import { useState, useEffect, useRef } from 'react'
import { useDeviceType } from 'components/hooks/useDeviceType'
import DragAndDrop from 'components/UI/dragAndDrop/DragAndDrop'
import DragAndDropPlaceholder from 'components/UI/dragAndDrop/DragAndDropPlaceholder'
import Button from 'components/UI/buttons/button/Button'
import { NotificationManager } from 'react-notifications'
import Strings from 'values/Strings'
import classes from './TeamManagementPage.module.css'

function ImageUploader({ selectedCommand, onUpload, loading, onRemove, removing }) {
    const [previewImage, setPreviewImage] = useState(null)
    const [serverImagePreview, setServerImagePreview] = useState(null)
    const [isServerImageLoading, setIsServerImageLoading] = useState(false)
    const serverImageObjectUrlRef = useRef(null)
    const [selectedFile, setSelectedFile] = useState(null)
    const dragZoneRef = useRef(null)
    const device = useDeviceType()
    const [dragZoneText, setDragZoneText] = useState(
        device === 'phone' 
            ? 'Нажмите для выбора изображения' 
            : 'Перетащите изображение сюда или нажмите для выбора'
    )
    const previousImageUrlRef = useRef(selectedCommand?.image_url)

    const clearServerImagePreview = () => {
        if (serverImageObjectUrlRef.current) {
            URL.revokeObjectURL(serverImageObjectUrlRef.current)
            serverImageObjectUrlRef.current = null
        }
        setServerImagePreview(null)
    }

    useEffect(() => {
        return () => {
            clearServerImagePreview()
        }
    }, [])

    useEffect(() => {
        if (!selectedCommand?.id) {
            clearServerImagePreview()
            setPreviewImage(null)
            setSelectedFile(null)
            setDragZoneText(
                device === 'phone' 
                    ? 'Нажмите для выбора изображения' 
                    : 'Перетащите изображение сюда или нажмите для выбора'
            )
            return
        }
        setPreviewImage(null)
        setSelectedFile(null)
        setDragZoneText(
            device === 'phone' 
                ? 'Нажмите для выбора изображения' 
                : 'Перетащите изображение сюда или нажмите для выбора'
        )
    }, [selectedCommand?.id, device])

    useEffect(() => {
        if (previousImageUrlRef.current && selectedCommand?.image_url && selectedCommand.image_url !== previousImageUrlRef.current) {
            handleClearPreview()
        }
        previousImageUrlRef.current = selectedCommand?.image_url
    }, [selectedCommand?.image_url])

    useEffect(() => {
        async function loadServerImage(publicKey) {
            try {
                if (!publicKey || !publicKey.startsWith('http')) {
                    clearServerImagePreview()
                    return
                }
                setIsServerImageLoading(true)
                const encodedKey = encodeURIComponent(publicKey)
                const metaResponse = await fetch(`https://cloud-api.yandex.net/v1/disk/public/resources?public_key=${encodedKey}`)
                const metaData = await metaResponse.json()
                const previewBase = metaData.preview ? metaData.preview.split('size')[0] : null
                const previewUrl = previewBase ? `${previewBase}size=1024x1024` : metaData.file
                if (!previewUrl) {
                    console.warn('⚠️ [ImageUploader] Не удалось получить превью для изображения')
                    clearServerImagePreview()
                    setIsServerImageLoading(false)
                    return
                }
                const imageResponse = await fetch(previewUrl)
                const blob = await imageResponse.blob()
                clearServerImagePreview()
                const objectUrl = URL.createObjectURL(blob)
                serverImageObjectUrlRef.current = objectUrl
                setServerImagePreview(objectUrl)
                setIsServerImageLoading(false)
            } catch (error) {
                console.error('❌ [ImageUploader] Ошибка загрузки изображения с Яндекса:', error)
                clearServerImagePreview()
                setIsServerImageLoading(false)
            }
        }

        if (selectedCommand?.image_url) {
            loadServerImage(selectedCommand.image_url)
        } else {
            clearServerImagePreview()
            setIsServerImageLoading(false)
        }
    }, [selectedCommand?.image_url])

    function handleImagePreview(file) {
        console.log('📸 [ImageUploader] handleImagePreview вызван:', { 
            fileName: file?.name, 
            fileType: file?.type, 
            fileSize: file?.size 
        })
        if (!file) {
            console.warn('⚠️ [ImageUploader] Файл не передан')
            return
        }
        
        // Проверяем тип файла
        if (!file.type.startsWith('image/')) {
            console.warn('⚠️ [ImageUploader] Неверный тип файла:', file.type)
            NotificationManager.warning('Пожалуйста, выберите изображение', Strings.notificationsWarningTitle, 3000)
            return
        }

        // Сохраняем файл
        console.log('💾 [ImageUploader] Сохранение файла для загрузки')
        setSelectedFile(file)
        
        // Создаем превью
        const reader = new FileReader()
        reader.onload = (e) => {
            console.log('🖼️ [ImageUploader] Превью создано, размер:', e.target.result?.length)
            setPreviewImage(e.target.result)
        }
        reader.onerror = (error) => {
            console.error('❌ [ImageUploader] Ошибка чтения файла:', error)
        }
        reader.readAsDataURL(file)
        setDragZoneText('Изображение выбрано')
    }

    function handleClearPreview() {
        console.log('↩️ [ImageUploader] Очистка выбранного изображения')
        setPreviewImage(null)
        setSelectedFile(null)
        setDragZoneText(
            device === 'phone' 
                ? 'Нажмите для выбора изображения' 
                : 'Перетащите изображение сюда или нажмите для выбора'
        )
    }

    function handleUpload() {
        console.log('🚀 [ImageUploader] handleUpload вызван:', {
            selectedCommandId: selectedCommand?.id,
            hasPreviewImage: !!previewImage,
            hasSelectedFile: !!selectedFile,
            fileName: selectedFile?.name
        })
        
        if (!selectedCommand?.id) {
            console.warn('⚠️ [ImageUploader] Команда не выбрана')
            NotificationManager.warning('Выберите команду', Strings.notificationsWarningTitle, 3000)
            return
        }

        if (!previewImage || !selectedFile) {
            console.warn('⚠️ [ImageUploader] Изображение не выбрано')
            NotificationManager.warning('Выберите изображение для загрузки', Strings.notificationsWarningTitle, 3000)
            return
        }

        console.log('✅ [ImageUploader] Запуск загрузки изображения')
        onUpload(selectedFile)
    }

    const hasServerImage = !!serverImagePreview

    return (
        <li>
            <div className={classes.uploadSection}>
                {isServerImageLoading ? (
                    <div className={classes.imageSkeleton}></div>
                ) : hasServerImage ? (
                    <div className={classes.imagePreview}>
                        <img
                            src={serverImagePreview}
                            alt="Фотография команды"
                            className={classes.previewImage}
                        />
                        {onRemove && (
                            <button
                                type="button"
                                className={classes.removeImageButton}
                                onClick={onRemove}
                                disabled={removing}
                                title="Удалить фотографию"
                            >
                                {removing ? '…' : '×'}
                            </button>
                        )}
                    </div>
                ) : previewImage ? (
                    <>
                        <div className={classes.imagePreview}>
                            <img
                                src={previewImage}
                                alt="Предпросмотр команды"
                                className={classes.previewImage}
                            />
                            <button
                                type="button"
                                className={classes.removeImageButton}
                                onClick={handleClearPreview}
                                title="Отменить выбор"
                            >
                                ×
                            </button>
                        </div>

                        <div className={classes.btn__style}>
                            <Button
                                onClick={handleUpload}
                                disabled={loading}
                            >
                                {loading ? 'Загрузка...' : 'Загрузить'}
                            </Button>
                        </div>
                    </>
                ) : (
                    <DragAndDrop
                        dragZoneRef={dragZoneRef}
                        borderStyle="dashed"
                        borderWidth={2}
                        borderColor="var(--main-color, #993b3b)"
                        borderRadius={device === 'phone' ? '12px' : '1vw'}
                        setDragZoneText={setDragZoneText}
                        textDragZone={device === 'phone' ? 'Нажмите для выбора изображения' : 'Перетащите изображение сюда или нажмите для выбора'}
                        textOnDragStart="Отпустите файл для загрузки"
                        imagePreview={(file) => {
                            if (file) {
                                handleImagePreview(file)
                            }
                        }}
                    >
                        <DragAndDropPlaceholder
                            dragZoneRef={dragZoneRef}
                            height={device === 'phone' ? '200px' : '20vw'}
                            imagePreview={(file) => {
                                if (file) {
                                    handleImagePreview(file)
                                }
                            }}
                        >
                            {dragZoneText}
                        </DragAndDropPlaceholder>
                    </DragAndDrop>
                )}

            </div>
        </li>
    )
}

export default ImageUploader

